<template>
  <v-dialog v-model="dialogVisible" max-width="500" persistent>
    <v-card class="area-form-card">
      <v-card-title class="primary white--text">
        <v-icon dark class="mr-2">{{ isEdit ? 'edit' : 'add_location' }}</v-icon>
        {{ isEdit ? 'Edit Area' : 'New Area' }}
        <v-spacer />
        <v-btn icon dark @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-text-field
            v-model="form.area_id"
            label="Area ID"
            prepend-icon="tag"
            :rules="[rules.required]"
            :disabled="isEdit"
            hint="Unique identifier (e.g., bne-office-1)"
            persistent-hint
            outlined
            dense
            class="mb-3"
          />

          <v-text-field
            v-model="form.area_name"
            label="Area Name"
            prepend-icon="business"
            :rules="[rules.required]"
            hint="Display name for this area"
            persistent-hint
            outlined
            dense
            class="mb-3"
          />

          <v-select
            v-model="form.timezone"
            :items="timezones"
            label="Timezone"
            prepend-icon="schedule"
            outlined
            dense
            class="mb-3"
          />

          <v-text-field
            v-model="form.client_property_id"
            label="Client Property ID"
            prepend-icon="vpn_key"
            hint="External reference ID (optional)"
            persistent-hint
            outlined
            dense
          />
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!valid"
          @click="save"
        >
          <v-icon left>save</v-icon>
          {{ isEdit ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AreaFormDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    area: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      valid: false,
      saving: false,
      form: {
        area_id: '',
        area_name: '',
        timezone: 'Australia/Brisbane',
        client_property_id: ''
      },
      rules: {
        required: v => !!v || 'This field is required'
      },
      timezones: [
        'Australia/Brisbane',
        'Australia/Sydney',
        'Australia/Melbourne',
        'Australia/Perth',
        'Australia/Adelaide',
        'Australia/Darwin',
        'Australia/Hobart',
        'Pacific/Auckland',
        'Asia/Singapore',
        'Asia/Tokyo',
        'Europe/London',
        'America/New_York',
        'America/Los_Angeles',
        'UTC'
      ]
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value)
          this.$emit('close')
      }
    },
    isEdit() {
      return !!this.area
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        if (this.area)
          this.form = {
            area_id: this.area.area_id || '',
            area_name: this.area.area_name || '',
            timezone: this.area.timezone || 'Australia/Brisbane',
            client_property_id: this.area.client_property_id || ''
          }
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        area_id: '',
        area_name: '',
        timezone: 'Australia/Brisbane',
        client_property_id: ''
      }
      if (this.$refs.form)
        this.$refs.form.resetValidation()
    },
    close() {
      this.$emit('close')
    },
    async save() {
      if (!this.$refs.form.validate())
        return

      this.saving = true
      try {
        if (this.isEdit)
          await axios.put(`/api/areas/${this.form.area_id}`, {
            area_name: this.form.area_name,
            timezone: this.form.timezone,
            client_property_id: this.form.client_property_id
          })
        else
          await axios.post('/api/areas', this.form)

        this.$emit('saved')
        this.close()
      } catch (error) {
        console.error('Failed to save area:', error)
        const message = (error.response && error.response.data && error.response.data.error) || 'Failed to save area'
        // eslint-disable-next-line no-alert
        window.alert(message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.area-form-card {
  border-radius: 12px !important;
}
</style>
